import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, cn, Dialog, Field, Input, toast } from "@dl/web-core-js/ui";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import {
  PurchasingClient,
  type PurchaseOrderDTO,
  type PurchaseOrderLineDTO,
  type ReceiptResult,
} from "../../api/purchasing";
import { isoDate } from "../../api/reporting";

/**
 * Goods arriving against an order.
 *
 * <p>Each line opens at what is still to come, because that is what usually
 * arrives; a short delivery is a number changed, and a line not in the box is
 * cleared. Receiving more than was ordered is allowed — it happens — and the
 * result says which lines went over. Quantities are in the unit the line was
 * ordered in; stock receives them through the line's own factor.</p>
 */
export function ReceiveDialog({
  order,
  lines,
  onClose,
  onPosted,
}: {
  order: PurchaseOrderDTO;
  lines: PurchaseOrderLineDTO[];
  onClose: () => void;
  onPosted: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const purchasing = useMemo(() => new PurchasingClient(http), [http]);
  // Expenses are "received" as deliveries: nothing reaches stock, but the
  // order still needs to know the repair was done or the parcel went.
  const items = lines.filter((line) => line.kind !== "TEXT");

  const [quantities, setQuantities] = useState<Record<number, string>>(() =>
    Object.fromEntries(items.map((line) => [line.id, Number(line.quantity_outstanding ?? 0) > 0 ? String(Math.round(Number(line.quantity_outstanding) * 10000) / 10000) : ""])),
  );
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(isoDate(new Date()));
  const [result, setResult] = useState<ReceiptResult | null>(null);

  const chosen = items
    .map((line) => ({ line, quantity: Number(quantities[line.id]) }))
    .filter(({ quantity }) => Number.isFinite(quantity) && quantity > 0);
  // A negative or unreadable figure is not "did not arrive": it is a typo, and
  // posting the other lines around it would hide it.
  const invalid = new Set(
    items
      .filter((line) => {
        const text = (quantities[line.id] ?? "").trim();
        return text !== "" && !(Number(text) >= 0);
      })
      .map((line) => line.id),
  );

  const post = useMutation({
    mutationFn: () =>
      purchasing.receive({
        purchase_order_id: order.id,
        supplier_id: null,
        warehouse_id: null,
        document_date: date || null,
        reference: reference.trim() || null,
        note: null,
        lines: chosen.map(({ line, quantity }) => ({
          purchase_order_line_id: line.id,
          part_id: null,
          quantity,
          unit_price: null,
          note: null,
        })),
      }),
    onSuccess: async (posted) => {
      setResult(posted);
      await onPosted();
      const consumed = posted.consumed ?? [];
      if (consumed.length > 0) {
        toast.success(
          t("po.received-consumed", "Receipt #{{id}} posted; {{count}} components used up at {{warehouse}}", {
            id: posted.stock_document_id,
            count: consumed.length,
            warehouse: consumed[0]?.warehouse_code ?? "",
          }),
        );
      } else {
        toast.success(t("po.received", "Receipt #{{id}} posted", { id: posted.stock_document_id }));
      }
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("po.receive.title", "Receive against order {{n}}", { n: order.number })}
      actions={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("button.close", "Close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button variant="primary" disabled={chosen.length === 0 || invalid.size > 0} loading={post.isPending} onClick={() => post.mutate()}>
              {chosen.length === 1
                ? t("po.receive.post-one", "Receive 1 line")
                : t("po.receive.post", "Receive {{n}} lines", { n: chosen.length })}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <table className="w-full text-[13px] tabular-nums">
          <thead className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
            <tr>
              <th className="py-1">{t("column.part.number", "Number")}</th>
              <th className="py-1 text-right">{t("po.receive.received", "Received")}</th>
              <th className="py-1 text-right">{t("po.receive.into-stock", "Into stock")}</th>
              <th className="py-1 text-right">{t("po.receive.still", "Still to come")}</th>
            </tr>
          </thead>
          <tbody>
            {result.lines.map((line) => (
              <tr key={`${line.part_number}-${line.stock_movement_id ?? "expense"}`} className="border-t border-line">
                <td className="py-1">
                  {line.part_number}
                  {line.over_received && (
                    <Badge variant="warning" size="sm" className="ml-2">
                      {t("po.receive.over", "over")}
                    </Badge>
                  )}
                </td>
                <td className="py-1 text-right">{formatNumber(line.quantity)}</td>
                <td className="py-1 text-right">{formatNumber(line.stock_quantity)}</td>
                <td className="py-1 text-right">{formatNumber(line.outstanding_after)}</td>
              </tr>
            ))}
          </tbody>
          {(result.consumed ?? []).length > 0 && (
            <tbody>
              <tr>
                <td colSpan={4} className="pb-1 pt-4 text-[11px] font-medium uppercase tracking-wide text-ink-faint">
                  {t("po.receive.consumed", "Used up by the subcontractor")}
                </td>
              </tr>
              {(result.consumed ?? []).map((used) => (
                <tr key={used.stock_movement_id} className="border-t border-line">
                  <td className="py-1">
                    {used.part_number}
                    <span className="ml-2 text-[11px] text-ink-faint">
                      {t("po.receive.consumed-for", "for {{part}}", { part: used.for_part_number })}
                    </span>
                  </td>
                  <td className="py-1 text-right" colSpan={2}>
                    −{formatNumber(used.quantity)}
                  </td>
                  <td className="py-1 text-right text-ink-muted">{used.warehouse_code}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("po.receive.slip", "Packing slip")} htmlFor="rcv-ref">
              <Input id="rcv-ref" value={reference} onChange={(event) => setReference(event.target.value)} />
            </Field>
            <Field label={t("form.field.date", "Date")} htmlFor="rcv-date">
              <Input id="rcv-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </Field>
          </div>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-[13px]">
              <thead className="bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-faint">
                <tr>
                  <th className="px-3 py-2">{t("column.part.number", "Number")}</th>
                  <th className="px-3 py-2 text-right">{t("column.bom.quantity", "Qty")}</th>
                  <th className="px-3 py-2 text-right">{t("po.receive.still", "Still to come")}</th>
                  <th className="px-3 py-2 text-right">{t("po.receive.now", "Arrived")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((line) => (
                  <tr key={line.id} className="border-t border-line">
                    <td className="px-3 py-1.5">
                      {line.kind === "EXPENSE" ? (
                        <span>
                          {line.description}
                          <span className="ml-2 text-[11px] text-ink-faint">{t("po.line.expense", "Expense")}</span>
                        </span>
                      ) : (
                        <>
                          <span className="font-medium">{line.part_number}</span>
                          <span className="ml-2 text-[11px] text-ink-faint">{line.part_name}</span>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(line.quantity)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(line.quantity_outstanding)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={quantities[line.id] ?? ""}
                        aria-invalid={invalid.has(line.id) || undefined}
                        onChange={(event) => setQuantities((current) => ({ ...current, [line.id]: event.target.value }))}
                        className={cn("ml-auto h-8 w-24 text-right", invalid.has(line.id) && "border-risk")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {invalid.size > 0 && (
            <p className="text-[12px] text-risk">{t("po.receive.invalid", "A quantity cannot be negative. Clear the line instead if it did not arrive.")}</p>
          )}
          <p className="text-[12px] text-ink-muted">
            {t("po.receive.hint", "Each line starts at what is still to come. Change it for a short delivery, clear it for a line that did not arrive.")}
          </p>
        </div>
      )}
    </Dialog>
  );
}
